import { useState } from 'react';
import { Link, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  HomeIcon,
  BanknotesIcon,
  ChartPieIcon,
  ChartBarSquareIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/24/outline';
import {
  HomeIcon as HomeIconSolid,
  BanknotesIcon as BanknotesIconSolid,
  ChartPieIcon as ChartPieIconSolid,
  ChartBarSquareIcon as ChartBarSquareIconSolid,
  EllipsisHorizontalIcon as EllipsisHorizontalIconSolid,
} from '@heroicons/react/24/solid';
import { MobileMenuDrawer } from './MobileMenuDrawer';

interface NavItem {
  path: string;
  labelKey: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  activeIcon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

const navItems: NavItem[] = [
  {
    path: '/dashboard',
    labelKey: 'nav.home',
    icon: HomeIcon,
    activeIcon: HomeIconSolid,
  },
  {
    path: '/finance',
    labelKey: 'nav.finance',
    icon: BanknotesIcon,
    activeIcon: BanknotesIconSolid,
  },
  {
    path: '/budgets',
    labelKey: 'nav.budgets',
    icon: ChartPieIcon,
    activeIcon: ChartPieIconSolid,
  },
  {
    path: '/reports',
    labelKey: 'nav.insights',
    icon: ChartBarSquareIcon,
    activeIcon: ChartBarSquareIconSolid,
  },
];

// Paths handled by the "More" menu drawer
const morePaths = ['/categories', '/savings', '/spending-plan', '/debts', '/banks', '/settings', '/recurring', '/import', '/transactions', '/accounts'];

export function MobileBottomBar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const moreActive = morePaths.some((p) => location.pathname.startsWith(p));

  return (
    <>
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 md:hidden safe-area-bottom"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="flex items-center justify-around h-16">
          {navItems.map((item) => {
            const active = isActive(item.path);
            const Icon = active ? item.activeIcon : item.icon;

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center justify-center flex-1 h-full min-w-0 px-1 transition-colors ${
                  active
                    ? 'text-brand-purple-600'
                    : 'text-gray-500 active:text-brand-purple-600'
                }`}
                style={{ minHeight: '44px', minWidth: '44px' }}
              >
                <Icon className="w-6 h-6 mb-0.5" />
                <span className="text-xs font-medium truncate max-w-full">
                  {t(item.labelKey)}
                </span>
              </Link>
            );
          })}

          {/* More button */}
          <button
            onClick={() => setMenuOpen(true)}
            className={`flex flex-col items-center justify-center flex-1 h-full min-w-0 px-1 transition-colors ${
              moreActive
                ? 'text-brand-purple-600'
                : 'text-gray-500 active:text-brand-purple-600'
            }`}
            style={{ minHeight: '44px', minWidth: '44px' }}
          >
            {moreActive ? (
              <EllipsisHorizontalIconSolid className="w-6 h-6 mb-0.5" />
            ) : (
              <EllipsisHorizontalIcon className="w-6 h-6 mb-0.5" />
            )}
            <span className="text-xs font-medium">{t('nav.more')}</span>
          </button>
        </div>
      </nav>
      <MobileMenuDrawer isOpen={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
}
